import Indicator, { IndicatorType } from '../../../app/builder/condition/indicators/types/Indicator'
import { CompositeSnapshot, CustomIndicatorDoc } from '../../../types/composite'
import { compositeIndicatorName, isCompositeName } from './refs'

/**
 * Builds a condition-selector registry entry for a custom indicator. The
 * existing selection handler clones `params` into the condition verbatim, so
 * `{id, version, snapshot}` is exactly what gets stored in the saved pattern:
 * a pinned immutable version plus an at-a-glance stub — the def's INTERNALS
 * are deliberately absent (size + IP: shared patterns must not disclose the
 * indicator's recipe). Engines resolve the pinned version server-side from
 * the top-level customIndicators collection.
 */
export const buildCompositeRegistryEntry = (
  doc: Pick<CustomIndicatorDoc, 'id' | 'name' | 'def'>
): Indicator => {
  const snapshot: CompositeSnapshot = {
    name: doc.name,
    outputs: doc.def.outputs.map((o) => o.field),
    minCandles: doc.def.minCandles,
    scale: doc.def.scale ?? 'oscillator',
  }
  return {
    // Custom-ness is identified by the composite: name prefix; `type` carries
    // the SCALE semantics the RHS pairing rules key on
    type: snapshot.scale === 'overlay' ? IndicatorType.OVERLAY : IndicatorType.OSCILLATOR,
    value: compositeIndicatorName(doc.id),
    label: doc.name,
    nickname: doc.name.length > 13 ? `${doc.name.slice(0, 12)}…` : undefined,
    fields: snapshot.outputs,
    params: { id: doc.id, version: doc.def.version, snapshot },
  }
}

const isValidSnapshot = (snapshot: any): snapshot is CompositeSnapshot =>
  !!snapshot &&
  typeof snapshot === 'object' &&
  typeof snapshot.name === 'string' &&
  Array.isArray(snapshot.outputs) &&
  snapshot.outputs.every((o: unknown) => typeof o === 'string')
// (scale is optional-tolerant: missing → oscillator)

/** Registry entry synthesized from a condition's embedded stub — how viewers
 * who don't own the indicator (shared/copied/showcase patterns) still get a
 * fully renderable name/outputs without ever seeing the internals. */
export const syntheticCompositeEntry = (name: string, snapshot: CompositeSnapshot): Indicator => ({
  type: snapshot.scale === 'overlay' ? IndicatorType.OVERLAY : IndicatorType.OSCILLATOR,
  value: name,
  label: snapshot.name,
  nickname: snapshot.name.length > 13 ? `${snapshot.name.slice(0, 12)}…` : undefined,
  fields: snapshot.outputs,
})

type ConditionSideLike =
  | { indicator?: { name?: string; params?: { snapshot?: unknown } } }
  | undefined

/** Resolves a condition side's indicator: the user's registry first, then the
 * side's own embedded snapshot for composites the viewer doesn't own.
 *
 * The registry is LATEST-version-only: a condition pinned to an older version
 * renders against the newest entry's outputs while the owner has the
 * indicator in their registry. If a republish renames/removes an output, the
 * old condition's field can look broken to its owner (viewers, resolving via
 * their snapshot, are unaffected). Acceptable for phase 1 — revisit by keying
 * resolution on id+version when the engines land. */
export const resolveSideIndicator = (
  side: ConditionSideLike,
  getIndicatorByValue: ((value: string | null | undefined) => Indicator | undefined) | undefined
): Indicator | undefined => {
  const name = side?.indicator?.name
  if (!name) return undefined
  const registryHit = getIndicatorByValue?.(name)
  if (registryHit) return registryHit
  const snapshot = side?.indicator?.params?.snapshot
  if (isCompositeName(name) && isValidSnapshot(snapshot)) {
    return syntheticCompositeEntry(name, snapshot)
  }
  return undefined
}
